import { getEncodedZendeskTicketId } from 'api/zendesk'

interface ZendeskTicketResponse {
  encodedTicketId: string | null
  error: string | null
}

/**
 * Extracts a Zendesk ticket ID from a URL or string
 * @param input - The URL or ticket ID string
 * @returns The extracted ticket ID or null if invalid
 */
export const extractZendeskTicketId = (input: string): string | null => {
  const trimmed = input.trim()
  if (!trimmed) {
    return null
  }
  const noQuery = trimmed.split('?')[0]
  const withPossibleTrailingSlash = noQuery?.split('/').filter(Boolean).pop() || ''
  const ticketId = withPossibleTrailingSlash?.replace(/\//g, '')

  // Handle empty string before conversion to number
  if (!ticketId) {
    return null
  }

  const idAsNumber = Number(ticketId)
  if (idAsNumber <= 0 || !Number.isInteger(idAsNumber)) {
    return null
  }

  return ticketId
}

/**
 * Processes a Zendesk ticket ID by extracting and encoding it
 * @param zendeskTicketId - The Zendesk ticket ID or URL
 * @returns An object containing the encoded ticket ID or any error message
 */
export async function processZendeskTicket(
  zendeskTicketId: string,
): Promise<ZendeskTicketResponse> {
  const extractedTicketId = extractZendeskTicketId(zendeskTicketId)

  if (!extractedTicketId) {
    return {
      encodedTicketId: null,
      error: 'Invalid Zendesk ticket ID or URL',
    }
  }

  const encodedZendeskTicketId = await getEncodedZendeskTicketId(extractedTicketId)

  if (!encodedZendeskTicketId) {
    return {
      encodedTicketId: null,
      error: 'Zendesk ticket not found',
    }
  }

  return {
    encodedTicketId: encodedZendeskTicketId,
    error: null,
  }
}
